import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, Avatar, Stack, IconButton, Divider, Tooltip } from "@mui/material";
import { useGlobalToast } from "components/GlobalToastProvider";
import {
  UserCircle,
  HandHeart,
  Shield,
  Phone,
  BellRinging,
  BellSimpleSlash,
  X,
  Clock,
  Translate,
} from "@phosphor-icons/react";
import getAvatarInitials from "utils/getAvatarInitials";
import { formatPhoneNumberWithCountryCode } from "utils/formatPhoneNumber";
import { formatTimeZoneToAbbreviation } from "utils/formatTimeZoneToAbbreviation";

const HeaderElement = ({ Icon, theme, label }) => (
  <Stack direction="row" gap="4px" alignItems="center">
    <Icon fontSize="16px" weight="duotone" color={theme.palette.text.secondary} />
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

const ThreadHeader = (props) => {
  const { showToast } = useGlobalToast();
  const theme = useTheme();
  const {
    teams,
    teamMembers,
    clientTeamMember,
    notificationsEnabled,
    refetchPreviews,
    currentUserId,
    refetchNotificationStatus,
    updateParticipantMutation,
    closeModal,
  } = props;

  const [localNotifState, setLocalNotifState] = useState(notificationsEnabled);
  const [pending, setPending] = useState(false);
  const hasMultipleTeams = teams?.length > 1;

  //needed to detect when the notification status is updated in the MessagesList component
  useEffect(() => {
    setLocalNotifState(notificationsEnabled);
  }, [notificationsEnabled]);

  const handleNotificationToggle = () => {
    if (pending) return;
    const prev = localNotifState;
    const next = !prev;

    setLocalNotifState(next);
    setPending(true);

    const updateData = {
      user_id: currentUserId,
      chat_id: clientTeamMember.id,
      update_data: {
        notifications_enabled: next,
      },
    };

    updateParticipantMutation(updateData, {
      onSuccess: () => {
        if (!notificationsEnabled) {
          showToast({
            message: `You'll be notified when ${clientTeamMember.firstName.trim()} sends new messages`,
          });
        }
        refetchNotificationStatus?.();
        setPending(false);

        //optionally update state in MessagesList component by triggering a refetch
        refetchPreviews?.();
      },
      onError: () => {
        setLocalNotifState(prev);
        setPending(false);
      },
    });
  };

  // If team member is on multiple teams, do not display badges in the header
  const memberData = teamMembers?.find((member) => member.userId === clientTeamMember.id);
  const showDesignatedCareGiver = !!memberData?.documentSigner && !hasMultipleTeams;
  const showLegalGuardian = !!memberData?.legalGuardian && !hasMultipleTeams;
  const rawTimeZone = memberData?.timezone ?? clientTeamMember?.timezone;
  const timeZone = formatTimeZoneToAbbreviation(rawTimeZone) || "----";
  const preferredLanguage = memberData?.preferredLanguage ?? clientTeamMember?.preferredLanguage ?? "English";
  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding="24px"
        width="100%"
      >
        <Stack alignItems="center" gap="12px" width="100%">
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Stack direction="row" alignItems="center" gap="8px">
              <Avatar sx={{ width: 32, height: 32 }}>
                {getAvatarInitials(clientTeamMember.name)}
              </Avatar>
              <Typography variant="h3">{clientTeamMember.name}</Typography>
            </Stack>
            {closeModal ? (
              <IconButton onClick={closeModal} size="small">
                <X />
              </IconButton>
            ) : (
              <Tooltip
                title={localNotifState ? "Turn off notifications" : "Notify me"}
                placement="top"
              >
                <IconButton onClick={handleNotificationToggle}>
                  {localNotifState ? (
                    <BellSimpleSlash weight="duotone" color={theme.palette.text.secondary} />
                  ) : (
                    <BellRinging weight="duotone" color={theme.palette.text.secondary} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Stack
            direction="row"
            alignSelf="flex-start"
            columnGap="16px"
            rowGap="8px"
            flexWrap="wrap"
          >
            {teams?.length >= 3 ? (
              <Tooltip title={teams.map((team) => team.clientName).join(", ")} placement="top">
                <Box>
                  <HeaderElement Icon={UserCircle} theme={theme} label={teams.length + " teams"} />
                </Box>
              </Tooltip>
            ) : (
              <HeaderElement
                Icon={UserCircle}
                theme={theme}
                label={
                  teams?.length === 2
                    ? `${teams[0]?.clientName}, ${teams[1]?.clientName}`
                    : teams?.[0]?.clientName ?? ""
                }
              />
            )}

            {showDesignatedCareGiver && (
              <HeaderElement Icon={HandHeart} theme={theme} label={"Designated Caregiver"} />
            )}
            {showLegalGuardian && (
              <HeaderElement Icon={Shield} theme={theme} label={"Legal Guardian"} />
            )}
            <HeaderElement Icon={Clock} theme={theme} label={timeZone} />
            <HeaderElement Icon={Translate} theme={theme} label={preferredLanguage} />
            <HeaderElement
              Icon={Phone}
              theme={theme}
              label={formatPhoneNumberWithCountryCode(clientTeamMember?.phoneNumber)}
            />
          </Stack>
        </Stack>
      </Box>
      <Divider />
    </>
  );
};

export default ThreadHeader;
